import { BehaviorSubject, Subject, debounceTime } from 'rxjs';

import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { K, Keyword } from './keyword';
import { KeywordEdit } from './keyword-edit';
import { KeywordGroup, KGPAR } from './keyword-group';
import { XItem } from './x-item';
import { StyleGroup } from './style-group';
import { Dataset } from './dataset';
import { ScriptSettings } from './script-settings';

export type KPAR = {
    keywordLs: K[];
    groupLs?: KGPAR[];
};

export class Keyword$ {
    private static readonly _EMPTY_SET = new Set<string>();

    currentEdit: KeywordEdit | undefined;

    currentStatsTab: 'keywords' | 'groups' = 'keywords';

    keywordEstimate$ = new BehaviorSubject({});

    private _keywordXitemMap = new Map<string, Map<string, Map<string, Set<string>>>>([
        ['main', new Map()],
        ['preview', new Map()],
    ]);

    private _kwMap = new Map<string, Keyword>();
    private _keywordGroupMap = new Map<string, KeywordGroup>();

    private _temporaryGroup: KeywordGroup | undefined;

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(private coords: CoordinatorService) {
        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    kwId2XItemIdSet(kwId: string, isPreview: boolean): Set<string> {
        return (
            this._keywordXitemMap
                .get(isPreview ? 'preview' : 'main')
                ?.get('kw2xitemMap')
                ?.get(kwId) || Keyword$._EMPTY_SET
        );
    }

    xitemId2KwIdSet(xitemId: string, isPreview: boolean): Set<string> {
        return (
            this._keywordXitemMap
                .get(isPreview ? 'preview' : 'main')
                ?.get('xitem2kwMap')
                ?.get(xitemId) || Keyword$._EMPTY_SET
        );
    }

    kwId2XItemLs(kwId: string, isPreview: boolean): XItem[] {
        const dt = isPreview ? this.coords.dataset$.preview : this.coords.dataset$.main;
        return dt.xitemLsFromIdLs(Array.from(this.kwId2XItemIdSet(kwId, isPreview)));
    }

    updateKwMap(params: any) {
        if (params.isUpdate) {
            ['preview', 'main'].forEach((pTag) => {
                const inputMp = params[pTag];
                const updatedMp = this._keywordXitemMap.get(pTag)!;

                ['kw2xitemMap', 'xitem2kwMap'].forEach((kTag) => {
                    inputMp.get(kTag)!.forEach((s: Set<string>, k: string) => {
                        // console.log("updateKwMap", updat edMp, k, s)
                        let finalMp = updatedMp.get(kTag);
                        if (!finalMp) {
                            finalMp = new Map();
                            updatedMp.set(kTag, finalMp);
                        }
                        let finalS = finalMp.get(k);
                        if (!finalS) {
                            finalS = new Set();
                            finalMp.set(k, finalS);
                        }
                        s.forEach((k) => finalS!.add(k));
                    });
                });
            });
        } else {
            ['preview', 'main'].forEach(
                (pTag) => params[pTag] && this._keywordXitemMap.set(pTag, params[pTag]),
            );
        }

        this.coords.display$.markForCheck();
    }

    get isHighlight() {
        return !this.coords.pin$.misc?.has('keywords-highlight-off-all');
    }

    set isHighlight(value: boolean) {
        const s = new ScriptSettings(this.coords);
        value ? s.keywords.show('all') : s.keywords.hide('all');
    }

    get hasTemporaryGroup(): boolean {
        return this._temporaryGroup !== undefined;
    }

    get temporaryGroup() {
        if (!this._temporaryGroup) {
            this._temporaryGroup = new KeywordGroup(this.coords, {
                title: this.coords.text('Group'),
            });
        }
        return this._temporaryGroup;
    }

    xitemKeywordLs(xitm: XItem): Keyword[] {
        return Array.from(this.xitemId2KwIdSet(xitm.id, xitm.dataset!.isPreview))
            .map((kwId) => this._kwMap.get(kwId)!)
            .filter((kw) => kw);
    }

    confirmTemporaryGroup() {
        if (this._temporaryGroup) {
            this._keywordGroupMap.set(this._temporaryGroup.id, this._temporaryGroup);
            this._temporaryGroup = undefined;
            this.markForManifestStore();
        }
    }

    private _exportParams() {
        const ls: [string, number][] = [];

        this.sortedKeywordLs.forEach((kw) => ls.push([kw.source, kw.size('main')]));

        return {
            headerLs: ['Keyword', 'Count'].map((x) => this.coords.text(x)),
            rowLs: ls,
            filename: `${this.coords.project$.currentTitle}_keywords`,
        };
    }

    html(isPartial = false) {
        this.coords.export$.html(this._exportParams());
    }

    csv(isPartial = false) {
        this.coords.export$.csv(this._exportParams());
    }

    export() {
        this.coords.script$.system$.exportKeywords(Array.from(this._kwMap.values()));
        // this.coords.manifest$.exportPart('keyword');
    }

    import() {
        this.coords.script$.system$.importKeywords();
        // this.coords.manifest$.importPart('keyword');
    }

    importSearchProcedure(obj: any) {
        const dct = obj.data;
        const newKwLs: Keyword[] = [];
        Object.keys(dct).forEach((grpTitle: string) => {
            const grp = new KeywordGroup(this.coords, {
                id: `__${grpTitle}`,
                title: grpTitle,
            });
            this._keywordGroupMap.set(grp.id, grp);
            const rgLs: string[] = dct[grpTitle];
            rgLs.forEach((rg) => {
                const kw = new Keyword(this.coords, {
                    id: `__${grpTitle}_${rg}`,
                    regexp: rg,
                    weight: 1,
                    groupId: grp.id,
                });
                this._kwMap.set(kw.id, kw);
                newKwLs.push(kw);
            });
            return grp;
        });

        this.markForManifestStore();
        this._updateKw2IdLs();
    }

    addKeywordGroup(grp: KeywordGroup) {
        this._keywordGroupMap.set(grp.id, grp);
    }

    addKeywords(kwLs: Keyword[], isReplace = false) {
        let hasChanges = false;

        kwLs.forEach((kw) => {
            if (isReplace) {
                const oldKw = this.getBySource(kw.source);
                if (oldKw && oldKw.id !== kw.id) {
                    this._kwMap.delete(oldKw.id);
                    hasChanges = true;
                }
                if (!this._kwMap.has(kw.id)) {
                    hasChanges = true;
                }
                this._kwMap.set(kw.id, kw);
            } else {
                // Enhanced deduplication - check both by source and by ID
                // This prevents keywords with same source but different IDs from being added
                // (which causes temporary UI duplication when reports are loaded)
                const existingBySource = this.getBySource(kw.source);
                const existingById = this._kwMap.get(kw.id);

                if (existingBySource) {
                    // Keyword with same source already exists - skip to prevent duplication
                    if (existingBySource.id !== kw.id) {
                        console.warn(
                            'Keyword$.addKeywords() - Duplicate source with different ID detected',
                            {
                                existingId: existingBySource.id,
                                newId: kw.id,
                                source: kw.source,
                            },
                        );
                    }
                    return;
                }

                if (existingById) {
                    // Keyword with same ID already exists (safety check)
                    return;
                }

                // No duplicate found, add the keyword
                hasChanges = true;
                this._kwMap.set(kw.id, kw);
            }
        });

        // Only trigger update if there were actual changes
        if (hasChanges) {
            this.markForUpdate();
        }
    }

    private _importTextFile(txt: string) {
        // console.log("TTT", txt)
    }

    private _importObject(dct: any) {
        const kwGrpTitleMap: Map<string, KeywordGroup> = new Map(
            Array.from(this._keywordGroupMap.values()).map((kwg) => [kwg.title, kwg]),
        );
        const kwSourceSet: Set<string> = new Set(
            Array.from(this._kwMap.values()).map((kw) => kw.source),
        );

        const importGrpId2currentGrpIdMap = new Map<string, string>();

        const newKwLs: Keyword[] = [];

        try {
            dct.groupLs.forEach((g: any) => {
                if (g.id === '_default') {
                    return;
                }
                const kwg = new KeywordGroup(this.coords, g);
                const currentGrp = kwGrpTitleMap.get(kwg.title);
                if (currentGrp) {
                    importGrpId2currentGrpIdMap.set(kwg.id, currentGrp.id);
                } else {
                    this._keywordGroupMap.set(kwg.id, kwg);
                }
            });
        } catch (e) {}
        try {
            dct.keywordLs.forEach((k: any) => {
                const kw = new Keyword(this.coords, k);
                if (kwSourceSet.has(kw.source)) {
                    return;
                }
                const currentGrpId = importGrpId2currentGrpIdMap.get(kw.groupId);
                if (currentGrpId) {
                    kw.groupId = currentGrpId;
                }
                newKwLs.push(kw);
                this._kwMap.set(kw.id, kw);
            });
        } catch (e) {}

        if (newKwLs.length) {
            this.markForUpdate();
        }
    }

    markForUpdate() {
        this.markForManifestStore();
        this.coords.highlight$.markForManifestStore();
        this._updateKw2IdLs();
    }

    private _importStyle(ls: any[]) {
        ls.forEach((dct) =>
            dct.styleLs.forEach((st: any) => {
                if (st.id.startsWith('_') || st.id.startsWith('kwg-_')) {
                    return;
                }
                const stg = new StyleGroup(this.coords, st);
                this.coords.highlight$.setStyle(stg);
            }),
        );
    }

    importResponse(params: any) {
        console.log('KKKKKKKKKK', params);
        const styleLs = params.resp.get('style');
        const kwLs = params.resp.get('keyword');
        const textLs = params.resp.get('text');

        styleLs && this._importStyle(styleLs);
        kwLs && kwLs.forEach((kw: any) => this._importObject(kw));

        // params.respLs.forEach((x: any) =>
        //     typeof x === "object" ? this._importObject(x) : this._importTextFile(x))
        this.coords.display$.markForCheck();
    }

    clear(includeGroups = true) {
        this.currentEdit = undefined;
        this._kwMap.clear();
        if (includeGroups) this.clearGroups();

        this._keywordXitemMap.get('preview')!.clear();
        this._keywordXitemMap.get('main')!.clear();
        this.coords.highlight$.clear();
        // this.refresh()
    }

    clearGroups() {
        this._keywordGroupMap.clear();
        this.refreshDefaultGroup();
        this.coords.highlight$.clear();
    }

    delete(kw: Keyword) {
        this._kwMap.delete(kw.id);
        this._keywordXitemMap.get('preview')!.delete(kw.id);
        this._keywordXitemMap.get('main')!.delete(kw.id);
        this.markForManifestStore();
        this.coords.highlight$.reset();
        this.coords.display$.closeDialog();
        this.coords.display$.markForCheck();
        this.coords.filter$.refreshKeywords();
    }

    deleteGroup(grp: KeywordGroup) {
        const keywordsToDelete = this.keywordLs.filter((kw) => kw.groupId === grp.id);
        keywordsToDelete.forEach((kw) => {
            this._kwMap.delete(kw.id);
            this._keywordXitemMap.get('preview')!.delete(kw.id);
            this._keywordXitemMap.get('main')!.delete(kw.id);
        });

        if (this.sortedGroupLs.length > 1) {
            this._keywordGroupMap.delete(grp.id);
        }
        this.markForManifestStore();
        this.coords.highlight$.reset();
        return this.sortedGroupLs[0];
    }

    create(params = {}) {
        return new Keyword(this.coords, params);
    }

    update(kw: Keyword, isStore = true) {
        this._kwMap.set(kw.id, kw);

        if (isStore) {
            // this.refresh()
            this.markForManifestStore();
            this.coords.highlight$.markForManifestStore();
            this._updateKw2IdLs();
        }
        this.coords.highlight$.reset();
        this.coords.display$.markForCheck();
        this.coords.filter$.refreshKeywords();
    }

    load3(params: any) {
        if (!params) {
            return;
        }

        params.groupLs?.forEach((grpParams: any) => {
            try {
                grpParams.isLoad = true;
                const kwg = new KeywordGroup(this.coords, grpParams);
                this._keywordGroupMap.set(kwg.id, kwg);
            } catch (e) {
                this.coords.utils$.consoleError('KEYWORD$ GROUP LOAD3 ERROR', e);
            }
        });
        params.keywordLs?.forEach((kwParams: any) => {
            try {
                const kw = new Keyword(this.coords, kwParams);
                this._kwMap.set(kw.id, kw);
            } catch (e) {
                this.coords.utils$.consoleError('KEYWORD$ KEYWORD LOAD3 ERROR', e);
            }
        });
    }

    updateDataset(dt: Dataset) {
        this._updateKw2IdLs(dt);
    }

    private async _updateKw2IdLs(dt?: Dataset) {
        const kwLs = Array.from(this._kwMap.values());
        const pr = {
            dt: this.coords.dataset$.preview,
            mp: this._keywordXitemMap.get('preview')!,
        };

        const col = {
            dt: this.coords.dataset$.main,
            mp: this._keywordXitemMap.get('main')!,
        };

        const dtLs = dt
            ? [dt.isPreview ? pr : col]
            : this.coords.stage$.isPreview
              ? [pr, col]
              : [col, pr];

        for (const x of dtLs) {
            const resp: any = await this.coords.worker$.autotag.call('regexp-search', {
                itemLs: x.dt.mainXitemLs.map((itm) => ({
                    id: itm.id,
                    textLs: [itm.title, itm.abstract],
                })),
                regexpLs: kwLs.map((kw) => ({ id: kw.id, regexp: kw.regexp })),
            });
            const rgMp = resp.result.mpRegexp2SourceIdMap;
            const itmIdMp = resp.result.mpSourceId2RegexpMap;

            x.mp.set('kw2xitemMap', rgMp);
            x.mp.set('xitem2kwMap', itmIdMp);

            // console.log('RG-MAP', rgMp);
            // console.log('ITM-ID-MAP', itmIdMp);
        }
        this.coords.highlight$.reset();
        this.coords.display$.markForCheck();
    }

    refreshDefaultGroup() {
        const grp = new KeywordGroup(this.coords, {
            id: '_default',
            title: this.coords.text('Default'),
        });
        this._keywordGroupMap.set(grp.id, grp);
        return grp;
    }

    private _manifestStore() {
        const valueMap = new Map<string, any[]>([
            ['keywordLs', this.keywordLs.map((kw) => kw.save())],
            ['groupLs', Array.from(this._keywordGroupMap.values()).map((grp) => grp.save())],
        ]);

        this.coords.manifest$.store({
            type: 'keyword',
            id: `keywordLs_${this.coords.project$.currentId}`,
            valueMap: valueMap,
        });
    }

    saveAsScript() {
        const ls: string[] = [];
        const sourceTxt = this.sortedKeywordLs.map((k) => k.source).join(', \n');
        ls.push(`const kwSources = \`\n${sourceTxt}\n\``);
        ls.push("keyword(kwSources.split(',').map(s => s.trim()), true)");

        this.sortedKeywordLs.forEach((k) => {
            if (k.weight !== 1) {
                ls.push(`keyword("${k.source}").weight=${k.weight}`);
            }
        });
        const txt = ls.map((x) => x.trim()).join('\n');
        this.coords.filesystem$.saveFile2(
            `keywords.mscript`,
            new Blob([txt], { type: 'text/plain' }),
        );
    }

    hasGroup(groupId: string) {
        return this._keywordGroupMap.has(groupId);
    }
    getGroup(groupId: string) {
        return this._keywordGroupMap.get(groupId);
    }

    getGroupByTitle(title: string) {
        title = title.trim().toLowerCase();
        for (const grp of this._keywordGroupMap.values()) {
            if (grp.title.toLowerCase() == title) {
                return grp;
            }
        }

        return null;
    }

    setGroup(grp: KeywordGroup) {
        this._keywordGroupMap.set(grp.id, grp);
        this.markForManifestStore();
    }

    getBySource(source: string) {
        for (const kw of this.keywordLs) {
            if (kw.source == source) {
                return kw;
            }
        }
        return null;
    }

    get keywordLs() {
        return Array.from(this._kwMap.values());
    }

    get sortedKeywordLs(): Keyword[] {
        return this.keywordLs.sort((a, b) => (a.sortTag < b.sortTag ? -1 : 1));
    }

    get groupLs() {
        return Array.from(this._keywordGroupMap.values());
    }

    get sortedGroupLs() {
        const ls = Array.from(this._keywordGroupMap.values());

        return ls.sort((a, b) => {
            if (a.id == '_default') {
                return -1;
            }
            if (b.id == '_default') {
                return 1;
            }
            return a.sortTag < b.sortTag ? -1 : 1;
        });
    }

    edit(kw?: Keyword, xitm?: XItem, kwGroup?: KeywordGroup) {
        if (!this.coords.acl$.isOn('keyword', 'create')) {
            return;
        }

        const kwEdit = new KeywordEdit(this.coords, kw, kwGroup);
        if (xitm) {
            kwEdit.testTitle = xitm.title;
            kwEdit.testAbstract = xitm.abstract;
        }
        if (!kw) {
            const txt = window.getSelection()?.toString().trim();
            if (txt) {
                kwEdit.source = txt;
            }
        }
        kwEdit.dialog();
    }

    groupEditor(kwg?: KeywordGroup) {
        if (!this.coords.acl$.isOn('keyword', 'create')) {
            return;
        }
        if (!kwg) {
            kwg = new KeywordGroup(this.coords, { title: '' });
            // this.setGroup(kwg)
        }
        this.coords.display$.dialog$.next({
            action: 'keyword-group',
            kwg: kwg,
        });
    }
}
